import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { paramCase } from 'change-case';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
// @mui
import { Box, Card, Avatar, Typography, CardContent, Stack, Link, Button, IconButton, Tooltip, Paper, Popover } from '@mui/material';
import { tooltipClasses } from '@mui/material/Tooltip';
// routes
import { PATH_HelpAndCommunication } from '../../../routes/paths';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// utils
import { fDate } from '../../../utils/formatTime';
// components
import Image from '../../../components/image';
import Iconify from '../../../components/iconify';
import TextMaxLine from '../../../components/text-max-line';
import Scrollbar from '../../../components/scrollbar';
import BlogPostTags from "./BlogPostTags";
import { ACCESSABLE_VALUE } from '../../../auth/utils';
// ------------------------------------------------------------------------------------------------------

BlogPostTile.propTypes = {
    index: PropTypes.number,
    post: PropTypes.object,
};
// ------------------------------------------------------------------------------------------------------

export default function BlogPostTile({ post, index }) {

    const isDesktop = useResponsive('up', 'md');
    const navigate = useNavigate();

    const { serverCoverImage, topicName, tags, description, updatedDate, topicTypeName, ownerFranchise, article } = post;

    const handleViewMore = () => {
        navigate(PATH_HelpAndCommunication.helpandcommunicationviewpageone(paramCase(post.id.toString())), { state: { rowData: post } });
    }

    return (
        <Card sx={{ display: 'flex', flexDirection: 'column', height: 250, width: 'auto', borderRadius: 1, cursor: 'pointer' }}>
            <Box sx={{ position: 'relative', height: '40%', width: '100%', mb: 1 }}>
                <Image alt="cover"
                    src={`/UploadFiles/HelpTopic/CoverImages/${serverCoverImage}`}
                    // ratio="4/3"
                    sx={{ objectFit: 'fill', width: '100%', height: '100%' }}
                    onClick={handleViewMore}
                />
            </Box>

            <Box sx={{ width: '100%', height: '60%' }}>
                <PostContent
                    title={topicName}
                    topictype={topicTypeName}
                    comment={description}
                    createdAt={updatedDate}
                    index={index}
                    franchise={ownerFranchise}
                    tags={tags}
                    handleViewMore={handleViewMore}
                    post={post}
                />
            </Box>
        </Card>
    );
}

// --------------------------------------------------------------------------------------------------------------

PostContent.propTypes = {
    topictype: PropTypes.string,
    title: PropTypes.string,
    comment: PropTypes.string,
    createdAt: PropTypes.string,
    tags: PropTypes.string,
    post: PropTypes.object,
    handleViewMore: PropTypes.func,
};

export function PostContent({ title, topictype, comment, createdAt, tags, handleViewMore, post }) {

    //  const linkTo = PATH_HelpAndCommunication.helpandcommunicationviewpage(paramCase(title));

    const loggedInUserDetails = useSelector(state => state.userdetails.userValue);
    const helpAndCommunicationPageAccess = loggedInUserDetails?.HelpAndCommunicationPageAccess?.[0];
    const hasViewMoreBlog = helpAndCommunicationPageAccess?.HasViewMoreBlog ?? '1';
    const hasDownloadBlog = helpAndCommunicationPageAccess?.HasDownloadBlog ?? '1';

    const CustomWidthTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))({
        [`& .${tooltipClasses.tooltip}`]: {
            maxWidth: 500,
        },
    });

    const handleDownloadClick = (serverDocName, docName) => {
        const url = `/UploadFiles/HelpTopic/Documents/${serverDocName}`
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            docName
        );

        document.body.appendChild(link);    // Append to html link element page

        link.click();   // Start download

        link.parentNode.removeChild(link);  // Clean up and remove the link
    }

    const DateChecker = (dateToCheck) => {
        const inputDate = new Date(dateToCheck);
        const currentDate = new Date();
        const timeDifference = currentDate - inputDate;
        const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
        return daysDifference <= 15;
    };


    return (
        <CardContent sx={{ p: 1, pb: 0 }}>
            <Stack flexWrap="wrap" direction="row" justifyContent="space-between" alignItems="space-between" >
                {/* <Iconify icon="foundation:burst-new" width='25px' height='25px' color="success.main" /> */}

                <Typography variant="caption" component="div" sx={{ color: 'text.disabled', pt: 0.5 }}>
                    {fDate(createdAt)}
                </Typography>

                {DateChecker(createdAt) &&
                    <>
                        <Iconify icon="clarity:new-line" width='25px' height='25px' color="success.main" sx={{ mr: 1 }} />
                    </>
                }
            </Stack>

            <TextMaxLine
                variant='body2'
                persistent
                color="blue"
                line={1}
                onClick={handleViewMore}
                sx={{
                    ":hover": {
                        cursor: "pointer",
                    },
                    fontSize: '0.80rem',
                    //  fontWeight: 'bold',
                }}
            >
                {title}
            </TextMaxLine>

            <Stack direction="row" alignItems="flex-start" justifyContent="flex-start">
                <CustomWidthTooltip title={comment}>
                    <Typography variant="caption"
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 3,
                            height: '2.7em',  // Set a fixed height for two lines
                            transition: 'all 0.3s ease',
                            fontSize: '0.62rem',
                        }}
                    >
                        {comment}
                    </Typography>
                </CustomWidthTooltip>
            </Stack>

            <Stack flexWrap="wrap" direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 0.5, height: '3em' }}>
                <Scrollbar>
                    <Paper>
                        <BlogPostTags post={post} />
                    </Paper>
                </Scrollbar>

                <Box sx={{ position: 'absolute', bottom: 2, right: 2 }} >
                    {topictype === 'Article' &&
                        <Tooltip title="View More" variant="solid">
                            <IconButton onClick={handleViewMore} disabled={hasViewMoreBlog !== ACCESSABLE_VALUE}>
                                <Iconify icon="carbon:view-filled" width='18px' height='18px'
                                    color={hasViewMoreBlog === ACCESSABLE_VALUE ? "primary.main" : "grey"}
                                />
                            </IconButton>
                        </Tooltip>
                    }

                    {topictype === 'Video' &&
                        <Tooltip title="Play Video" variant="solid">
                            <IconButton onClick={handleViewMore} disabled={hasViewMoreBlog !== ACCESSABLE_VALUE}>
                                <Iconify icon="fluent:video-28-filled" width='18px' height='18px'
                                    color={hasViewMoreBlog === ACCESSABLE_VALUE ? "primary.main" : "grey"}
                                />
                            </IconButton>
                        </Tooltip>
                    }

                    {topictype === 'Document' &&
                        <>
                            <Tooltip title="View Document" variant="solid">
                                <IconButton onClick={handleViewMore} disabled={hasViewMoreBlog !== ACCESSABLE_VALUE}>
                                    <Iconify icon="basil:document-solid" width='18px' height='18px'
                                        color={hasViewMoreBlog === ACCESSABLE_VALUE ? "primary.main" : "grey"}
                                    />
                                </IconButton>
                            </Tooltip>

                            {post.allowDownload &&
                                <Tooltip title="Download Document" variant="solid">
                                    <IconButton onClick={() => { handleDownloadClick(post.serverDocument, post.document) }}
                                        disabled={hasDownloadBlog !== ACCESSABLE_VALUE}
                                    >
                                        <Iconify icon="ic:baseline-download" width='18px' height='18px'
                                            color={hasDownloadBlog === ACCESSABLE_VALUE ? "primary.main" : "grey"}
                                        />
                                    </IconButton>
                                </Tooltip>
                            }
                        </>
                    }

                </Box>
            </Stack>
        </CardContent >
    );
}
